import { useContext } from 'react';
import { ListerContext } from './listerContext';
import ajaxRequest, { toQueryParams } from '../../helpers/ajaxRequest';
import { API, ListerData } from '../../types';
import { ListerActionTypes } from './listerReducer';
import initGlobalUtils from '../../utils/globalUtils';

const { isSearchResultsPage, listerData } = window.globalLister;

interface useListerApiMethods {
  selectSort(sortCode: string): Promise<ListerData>;
  selectFacet(location?: string): Promise<ListerData>;
  showNextProducts({ page: number }): Promise<ListerData>;
  showPreviousProducts({ page: number }): Promise<ListerData>;
}

export default function useListerApi(): useListerApiMethods {
  const [{ currentQuery, sorts }, dispatch] = useContext(ListerContext);

  const selectSort = (sortCode: string): Promise<ListerData> =>
    fetchListerData({ sort: sortCode, page: 1 }).then((response) => {
      dispatch({ type: ListerActionTypes.SET_LISTER_RESULTS, ...response });
      initGlobalUtils(response.pagination.sort as string, response.results);
      if (response?.currentQuery?.location) {
        const data = {
          q: currentQuery.location,
          text: currentQuery.freeTextSearch,
          ...{ sort: sortCode, page: 1 }
        };
        if (window.location.search !== `?${toQueryParams(data)}`) {
          window.history.pushState(null, '', `?${toQueryParams(data)}`);
        }
      }
      return response;
    });
  const selectFacet = (location = ''): Promise<ListerData> =>
    fetchListerData({ q: location, page: 1 }).then((response) => {
      dispatch({ type: ListerActionTypes.SET_LISTER_RESULTS, ...response });
      initGlobalUtils(response.pagination.sort as string, response.results);
      if (response?.currentQuery?.location) {
        const data = {
          text: currentQuery.freeTextSearch,
          sort: sorts.find((option) => option.selected)?.code || '',
          ...{ q: location, page: 1 }
        };
        if (window.location.search !== `?${toQueryParams(data)}`) {
          window.history.pushState(null, '', `?${toQueryParams(data)}`);
        }
      }
      return response;
    });

  const showNextProducts = ({ page }): Promise<ListerData> =>
    fetchListerData({ page }).then((response) => {
      dispatch({ type: ListerActionTypes.APPEND_LISTER_RESULTS, ...response });
      initGlobalUtils(response.pagination.sort as string, response.results);
      return response;
    });

  const showPreviousProducts = ({ page }): Promise<ListerData> =>
    fetchListerData({ page }).then((response) => {
      dispatch({ type: ListerActionTypes.PREPEND_LISTER_RESULTS, ...response });
      initGlobalUtils(response.pagination.sort as string, response.results);
      return response;
    });

  function fetchListerData(customQuery: Partial<{ q: string; sort: string; page: number }>): Promise<ListerData> {
    dispatch({ type: ListerActionTypes.SET_LISTER_DISABLED, isListerDisabled: true });
    const data = {
      q: currentQuery.location,
      text: currentQuery.freeTextSearch,
      page: currentQuery.currentPage,
      sort: sorts.find((option) => option.selected)?.code || '',
      ...customQuery
    };

    return ajaxRequest({
      method: 'GET',
      url: isSearchResultsPage
        ? API.SEARCH_ATTRAQT
        : `${API.CATEGORY_ATTRAQT.replace(':categoryCode', listerData.categoryCode || '')}`,
      data
    }).catch(() => {
      /* TODO: add error handling */
      dispatch({ type: ListerActionTypes.SET_LISTER_DISABLED, isListerDisabled: false });
    });
  }

  return { selectSort, selectFacet, showNextProducts, showPreviousProducts };
}
